import React, {useEffect, useRef, useState} from 'react';
import {FaPlus, FaSearch} from 'react-icons/fa';
import DOMPurify from 'dompurify';
import './AddSongComponent.css';
import {searchForSongs} from "../services/songs-db";
import {AddSongRequestedEvent} from "../../../infrastructure/events/channels";
import {useApplicationEvents} from "../../../infrastructure/events/useBroadcastChannel";

/**
 * Component for adding a new song at the end of the file
 */
/** @deprecated Adding songs from the browser is retired for Adorans. */
export const AddSongComponent = ({selectedFile}) => {
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isIndexBuilt, setIsIndexBuilt] = useState(false);

    const inputRef = useRef(null);
    const addSongRequested = useApplicationEvents(AddSongRequestedEvent)

    // Log when component mounts/unmounts
    useEffect(() => {
        console.log('🟢 AddSongComponent MOUNTED for file:', selectedFile?.name);
        return () => {
            console.log('🟠 AddSongComponent UNMOUNTED');
        };
    }, [selectedFile?.name]);

    // Focus input when search is opened
    useEffect(() => {
        if (isSearchFocused) {
            inputRef.current?.focus();

            // Jeśli otwieramy wyszukiwanie po raz pierwszy, wymuszamy przebudowę indeksu
            if (!isIndexBuilt) {
                console.log('Wymuszam przebudowę indeksu przy pierwszym otwarciu wyszukiwania');
                // Wywołaj wyszukiwanie z pustym zapytaniem, ale z wymuszeniem przebudowy indeksu
                searchForSongs('', true);
                setIsIndexBuilt(true);
            }
        }
    }, [isSearchFocused, isIndexBuilt]);

    const handleFocus = () => {
        setIsSearchFocused(true);
    };

    const handleBlur = () => {
        // Adding a delay so click events on search results can fire
        setTimeout(() => {
            setIsSearchFocused(false);
            setInputValue('');
            setSearchResults([]);
        }, 200);
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        if (value.trim()) {
            // Używamy searchForSongs z parametrem forceReindex=true przy pierwszym wyszukiwaniu
            const results = searchForSongs(value, !isIndexBuilt);
            setSearchResults(results);
            setIsIndexBuilt(true);
        } else {
            setSearchResults([]);
        }
    };

    const handleSongSelect = (song, event) => {
        // Prevent event bubbling
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] AddSongComponent: handleSongSelect called for song:`, song.fileName, song.songNumber);
        console.trace('handleSongSelect call stack');

        // Wysyłanie danych o pliku źródłowym, numerze piosenki i pliku docelowym
        addSongRequested.postMessage({
            sourceFileName: song.fileName,       // Nazwa pliku źródłowego
            songNumber: song.songNumber,         // Numer piosenki w pliku źródłowym
            targetFileName: selectedFile.name     // Nazwa pliku docelowego (aktualnie otwarty plik)
        });

        // Reset search
        setIsSearchFocused(false);
        setInputValue('');
        setSearchResults([]);
    };

    const highlightTerms = (text, terms) => {
        if (!text || !terms || terms.length === 0) return text;
        const regex = new RegExp(`(${terms.join('|')})`, 'giu');
        return text && DOMPurify.sanitize(text.replace(regex, match => `<mark>${match}</mark>`));
    };

    return (
        <div className="song add-song-container">
            {!isSearchFocused ? (
                <div className="add-song-row">
                    <h3>
                        <span className="song-number-placeholder">+</span>.{' '}
                        <span className="title add-song-title" onClick={handleFocus} title="Dodaj nową piosenkę">
                            <FaPlus style={{ marginRight: '8px', fontSize: '0.8em' }} />
                            Dodaj piosenkę
                        </span>
                    </h3>
                </div>
            ) : (
                <div className="add-song-search">
                    <div className="search-input-container">
                        <FaSearch className="search-icon"/>
                        <input
                            ref={inputRef}
                            type="search"
                            name="add-song-search"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="none"
                            spellCheck={false}
                            inputMode="search"
                            enterKeyHint="search"
                            value={inputValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Wyszukaj piosenkę do dodania..."
                            className="add-song-input"
                        />
                    </div>

                    {searchResults.length > 0 && (
                        <ul className="add-song-results">
                            {searchResults.map((result) => (
                                <li
                                    key={result.id}
                                    className="add-song-result-item"
                                >
                                    <button
                                        className="add-song-button"
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // Prevent input blur
                                            handleSongSelect(result, e);
                                        }}
                                        aria-label="Dodaj piosenkę"
                                    >
                                        <FaPlus />
                                    </button>
                                    <div className="result-content">
                                        <div className="result-file-name">{result.fileName}</div>
                                        <p dangerouslySetInnerHTML={{__html: highlightTerms(result.content, result.terms)}}></p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default AddSongComponent;
